import { MINUTE } from './time';

/**
 * What an observer receives: which cache, and which key.
 *
 * @remarks
 * A {@link Cache} reports its single entry under the key `''`.
 */
export interface CacheEvent {
  /** The key the event is about. */
  readonly key: string;
  /** The `name` the cache was created with. */
  readonly name: string;
}

/**
 * Callbacks for what happens inside a cache, for logging or metrics.
 *
 * @remarks
 * A throw from an observer is caught and dropped, so a failing log line cannot
 * break a read or a write. Each callback runs synchronously, inside the call
 * that caused it.
 */
export interface CacheObservers {
  /**
   * Called when a write pushes the least recently used entry out to stay
   * within `maxEntries`, whether or not that entry had expired. Removing an
   * expired entry for any other reason reports nothing.
   */
  readonly evict?: (event: CacheEvent) => void;
  /** Called when `get` or `getOrLoad` finds a live entry. */
  readonly hit?: (event: CacheEvent) => void;
  /** Called when `get` or `getOrLoad` finds no live entry. `has` reports neither. */
  readonly miss?: (event: CacheEvent) => void;
}

/** Options for one write. */
export interface CacheEntryOptions {
  /**
   * How long this entry lives, in milliseconds, instead of the cache-wide
   * `ttl`. `0` stores nothing and removes an existing entry under the key.
   */
  readonly ttl?: number;
}

/** Options for a {@link CacheMap}. */
export interface CacheMapOptions {
  /**
   * The current time in milliseconds. Defaults to `Date.now()`, read on every
   * call, so jest fake timers move it too.
   */
  readonly clock?: () => number;
  /**
   * The most entries the map holds, a whole number of 1 or more. Left out,
   * the map has no bound.
   */
  readonly maxEntries?: number;
  /** A non-empty label, used in error messages and in every {@link CacheEvent}. */
  readonly name: string;
  /** Callbacks for hits, misses and evictions. */
  readonly on?: CacheObservers;
  /**
   * How often, in milliseconds, a timer removes expired entries from memory, a
   * finite number above 0. Defaults to one minute. The timer starts with the
   * first entry that can expire, stops on the first round that finds none left,
   * and does not keep the process alive.
   */
  readonly sweepInterval?: number;
  /**
   * How long an entry lives, in milliseconds, a finite number of 0 or more.
   * Left out, an entry lives until it is deleted or evicted. `0` stores
   * nothing.
   */
  readonly ttl?: number;
}

interface Entry<T> {
  readonly expiresAt: number;
  readonly value: T;
}

type EventKind = keyof CacheObservers;

const DEFAULT_SWEEP_INTERVAL = MINUTE;

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    (typeof value === 'object' || typeof value === 'function') &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  );
}

function checkName(name: unknown): string {
  if (typeof name !== 'string' || name === '') {
    throw new TypeError('CacheMap: name must be a non-empty string');
  }
  return name;
}

function checkTtl(label: string, ttl: number | undefined): void {
  if (ttl === undefined) return;
  if (!Number.isFinite(ttl) || ttl < 0) {
    throw new RangeError(
      `${label}: ttl must be a finite number of 0 or more, got ${ttl}. Leave it out for no expiry.`,
    );
  }
}

/**
 * Holds a value per string key for a limited time.
 *
 * @remarks
 * An expired entry counts as absent everywhere: `get`, `has`, `delete`,
 * `getOrLoad` and `size` remove it when they meet it. A timer removes the
 * expired entries nobody meets, every `sweepInterval`, so keys that are never
 * read again do not stay in memory. It starts with the first entry that can
 * expire and stops on the first round that finds none left, or on `clear`.
 *
 * Above `maxEntries` the expired entries go first, without an event. Is the
 * map still above it, the least recently used entry is evicted. A hit through
 * `get` or `getOrLoad` and every `set` count as a use, `has` does not.
 *
 * The map holds values, not work in progress. `getOrLoad` takes a synchronous
 * load only. An async caller fetches the value itself and calls `set`, and
 * decides itself whether concurrent callers share one fetch. Storing a promise
 * in a `CacheMap<Promise<X>>` is not supported: a rejected promise would be
 * kept and handed out like any other value.
 *
 * @typeParam T - The type of the values the map holds.
 *
 * @example
 * ```ts
 * const settings = new CacheMap<string>({ name: 'app-config', ttl: 5 * MINUTE, maxEntries: 500 });
 * settings.set('feature-x', 'on');
 * settings.get('feature-x');
 * ```
 */
export class CacheMap<T> {
  readonly #name: string;
  readonly #ttl: number | undefined;
  readonly #maxEntries: number | undefined;
  readonly #clock: () => number;
  readonly #on: CacheObservers;
  readonly #sweepInterval: number;
  readonly #entries = new Map<string, Entry<T>>();
  #sweepTimer: ReturnType<typeof setInterval> | undefined;

  /**
   * @param options - The name, and optionally the ttl, the bound, the clock
   *   and the observers.
   * @throws TypeError when `name` is not a non-empty string.
   * @throws RangeError when `ttl` is not a finite number of 0 or more,
   *   `maxEntries` is not a whole number of 1 or more, or `sweepInterval` is
   *   not a finite number above 0.
   */
  constructor(options: CacheMapOptions) {
    this.#name = checkName(options.name);
    checkTtl(this.#label(), options.ttl);
    if (
      options.maxEntries !== undefined &&
      !(Number.isInteger(options.maxEntries) && options.maxEntries >= 1)
    ) {
      throw new RangeError(
        `${this.#label()}: maxEntries must be a whole number of 1 or more, got ${
          options.maxEntries
        }`,
      );
    }
    const sweepInterval = options.sweepInterval ?? DEFAULT_SWEEP_INTERVAL;
    if (!(Number.isFinite(sweepInterval) && sweepInterval > 0)) {
      throw new RangeError(
        `${this.#label()}: sweepInterval must be a finite number above 0, got ${sweepInterval}`,
      );
    }
    this.#sweepInterval = sweepInterval;
    this.#ttl = options.ttl;
    this.#maxEntries = options.maxEntries;
    this.#clock = options.clock ?? (() => Date.now());
    this.#on = options.on ?? {};
  }

  /**
   * The value under `key`, or `undefined` when there is no live entry.
   *
   * @remarks
   * A hit marks the key as recently used and reports `hit`, an absent key
   * reports `miss`.
   *
   * @param key - The key to read.
   * @returns The stored value, or `undefined`.
   * @throws TypeError when `key` is not a string.
   */
  get(key: string): T | undefined {
    const entry = this.#take(key);
    return entry?.value;
  }

  /**
   * Whether a live entry exists under `key`. Reports no event and does not
   * mark the key as used.
   *
   * @param key - The key to look for.
   * @returns `true` when the key has an entry that has not expired.
   * @throws TypeError when `key` is not a string.
   */
  has(key: string): boolean {
    return this.#live(key) !== undefined;
  }

  /**
   * Stores `value` under `key`, replacing what was there.
   *
   * @remarks
   * The entry lives for `options.ttl` when given, else for the cache-wide
   * `ttl`. A ttl of `0` stores nothing and removes the existing entry. When the
   * write takes the map above `maxEntries`, the expired entries are removed
   * first. Is the map still above it, the least recently used entry is evicted
   * and `evict` is reported.
   *
   * @param key - The key to store under.
   * @param value - The value to store.
   * @param options - A ttl for this entry only.
   * @throws TypeError when `key` is not a string.
   * @throws RangeError when `options.ttl` is not a finite number of 0 or more.
   */
  set(key: string, value: T, options?: CacheEntryOptions): void {
    this.#checkKey(key);
    checkTtl(this.#label(), options?.ttl);
    const ttl = options?.ttl ?? this.#ttl;
    const now = this.#clock();

    this.#entries.delete(key);
    if (ttl === 0) return;

    this.#entries.set(key, {
      expiresAt: ttl === undefined ? Infinity : now + ttl,
      value,
    });
    if (ttl !== undefined) this.#startSweeping();
    this.#evictAboveBound();
  }

  /**
   * Removes the entry under `key`, so the next `getOrLoad` loads again.
   *
   * @param key - The key to remove.
   * @returns `true` when a live entry was removed, `false` when there was none
   *   or it had expired.
   * @throws TypeError when `key` is not a string.
   */
  delete(key: string): boolean {
    const wasLive = this.#live(key) !== undefined;
    this.#entries.delete(key);
    return wasLive;
  }

  /** Removes every entry. Reports no event. */
  clear(): void {
    this.#entries.clear();
    this.#stopSweeping();
  }

  /**
   * Ends the map's use: removes every entry and stops the cleanup timer.
   *
   * @remarks
   * The same as `clear`, under the name a lifecycle hook looks for. The timer
   * never keeps the process alive, so calling this is not required, but a
   * service that shuts down can say explicitly that it is done with the map.
   * A later `set` with a ttl starts the timer again.
   */
  dispose(): void {
    this.clear();
  }

  /**
   * The number of live entries. Removes every expired entry first, so it
   * walks the whole map.
   */
  get size(): number {
    this.#removeExpired();
    return this.#entries.size;
  }

  /**
   * The value under `key`, or the result of `load`, stored before it is
   * returned.
   *
   * @remarks
   * A live entry is a hit and `load` is not called. Otherwise `miss` is
   * reported and `load` runs. When `load` throws, the error goes through and
   * nothing is stored. When `load` returns a promise or another thenable,
   * nothing is stored and a `TypeError` is thrown: fetch an async value
   * yourself and call `set`. A rejection of that promise is handled here, so it
   * does not surface as an unhandled rejection.
   *
   * @param key - The key to read or fill.
   * @param load - A synchronous function that produces the value.
   * @param options - A ttl for the entry, when it is loaded.
   * @returns The stored or the freshly loaded value.
   * @throws TypeError when `key` is not a string, or when `load` returns a
   *   thenable.
   * @throws RangeError when `options.ttl` is not a finite number of 0 or more.
   */
  getOrLoad(key: string, load: () => T, options?: CacheEntryOptions): T {
    checkTtl(this.#label(), options?.ttl);
    const entry = this.#take(key);
    if (entry !== undefined) return entry.value;

    const value = load();
    if (isThenable(value)) {
      Promise.resolve(value).catch(() => {});
      throw new TypeError(
        `${this.#label()}: getOrLoad takes a synchronous load. Fetch async values yourself and call set().`,
      );
    }
    this.set(key, value, options);
    return value;
  }

  #label(): string {
    return `CacheMap "${this.#name}"`;
  }

  #checkKey(key: unknown): void {
    if (typeof key !== 'string') {
      throw new TypeError(
        `${this.#label()}: a key must be a string, got ${typeof key}`,
      );
    }
  }

  /** The live entry under `key`, after removing it when it has expired. */
  #live(key: string): Entry<T> | undefined {
    this.#checkKey(key);
    const entry = this.#entries.get(key);
    if (entry === undefined) return undefined;
    if (this.#clock() >= entry.expiresAt) {
      this.#entries.delete(key);
      return undefined;
    }
    return entry;
  }

  /** A read that counts: reports hit or miss and marks a hit as used. */
  #take(key: string): Entry<T> | undefined {
    const entry = this.#live(key);
    if (entry === undefined) {
      this.#emit('miss', key);
      return undefined;
    }
    this.#entries.delete(key);
    this.#entries.set(key, entry);
    this.#emit('hit', key);
    return entry;
  }

  /**
   * Removes every expired entry, without an event.
   *
   * @returns Whether an entry that can still expire is left.
   */
  #removeExpired(): boolean {
    const now = this.#clock();
    let canExpire = false;
    // Deleting the current key while iterating a Map is safe in JavaScript.
    for (const [key, entry] of this.#entries) {
      if (now >= entry.expiresAt) this.#entries.delete(key);
      else if (entry.expiresAt !== Infinity) canExpire = true;
    }
    return canExpire;
  }

  #startSweeping(): void {
    if (this.#sweepTimer !== undefined) return;
    this.#sweepTimer = setInterval(() => {
      if (!this.#removeExpired()) this.#stopSweeping();
    }, this.#sweepInterval);
    // In Node the handle can be unref'd, so a forgotten cache never holds a
    // process open. A browser returns a number, which has nothing to unref.
    const handle: unknown = this.#sweepTimer;
    if (
      typeof handle === 'object' &&
      handle !== null &&
      'unref' in handle &&
      typeof handle.unref === 'function'
    ) {
      handle.unref();
    }
  }

  #stopSweeping(): void {
    clearInterval(this.#sweepTimer);
    this.#sweepTimer = undefined;
  }

  #evictAboveBound(): void {
    if (this.#maxEntries === undefined) return;
    if (this.#entries.size <= this.#maxEntries) return;
    // An expired entry counts as absent, and one that was read before it
    // expired sits behind live entries, so it goes before a live one is evicted.
    this.#removeExpired();
    while (this.#entries.size > this.#maxEntries) {
      const next = this.#entries.keys().next();
      if (next.done) return;
      this.#entries.delete(next.value);
      this.#emit('evict', next.value);
    }
  }

  #emit(kind: EventKind, key: string): void {
    try {
      this.#on[kind]?.({ key, name: this.#name });
    } catch {
      // An observer is for logging and metrics; its failure must not reach
      // the caller of the cache.
    }
  }
}
